export const UserGroupEnum = {
  Admins: 1,
  Managers: 2,
  Drivers: 3,
  OCCOperators: 4,
} as const

export type UserGroupEnum = typeof UserGroupEnum[keyof typeof UserGroupEnum]

export type AppRole = UserGroupEnum

const VALID_ROLES = new Set<number>(Object.values(UserGroupEnum))

/** Converte o `jobTitle` (numérico, vindo do backend como string) num `AppRole` válido, ou `null`. */
export function parseRole(jobTitle: string | number | null | undefined): AppRole | null {
  const raw = Number(jobTitle)
  if (!Number.isFinite(raw)) return null
  if (!VALID_ROLES.has(raw)) return null
  return raw as AppRole
}

/**
 * Rótulo de `user_role` para analytics (PostHog). O plano de tagueamento assume só
 * administrador/gestor, mas o app hoje deixa Motorista e Operador autenticarem no
 * Backoffice também (ver ROUTE_ACCESS) — mandamos o valor real para evidenciar isso.
 */
export const ANALYTICS_ROLE_LABELS: Record<AppRole, string> = {
  [UserGroupEnum.Admins]: 'administrador',
  [UserGroupEnum.Managers]: 'gestor',
  [UserGroupEnum.Drivers]: 'motorista',
  [UserGroupEnum.OCCOperators]: 'operador',
}

export interface RouteAccess {
  path: string
  roles: AppRole[]
}

export const ROUTE_ACCESS: RouteAccess[] = [
  { path: '/painel', roles: [UserGroupEnum.Admins, UserGroupEnum.Managers, UserGroupEnum.OCCOperators] },
  { path: '/viagens', roles: [UserGroupEnum.Admins, UserGroupEnum.Managers, UserGroupEnum.OCCOperators] },
  { path: '/empresas', roles: [UserGroupEnum.Admins] },
  { path: '/grupos-empresariais', roles: [UserGroupEnum.Admins] },
  { path: '/bases-operacionais', roles: [UserGroupEnum.Admins] },
  { path: '/usuarios', roles: [UserGroupEnum.Admins, UserGroupEnum.Managers] },
  { path: '/motoristas', roles: [UserGroupEnum.Admins, UserGroupEnum.Managers] },
  { path: '/veiculos', roles: [UserGroupEnum.Admins, UserGroupEnum.Managers] },
  { path: '/veiculo-categorias', roles: [UserGroupEnum.Admins] },
  { path: '/veiculo-tipos', roles: [UserGroupEnum.Admins] },
  { path: '/parametros', roles: [UserGroupEnum.Admins] },
  { path: '/relatorios', roles: [UserGroupEnum.Admins, UserGroupEnum.Managers] },
  { path: '/teste-viagens-xlsx', roles: [UserGroupEnum.Admins, UserGroupEnum.Managers] },
  { path: '/recalcular-eventos', roles: [UserGroupEnum.Admins, UserGroupEnum.Managers] },
]

export function findRouteAccess(pathname: string): RouteAccess | undefined {
  return ROUTE_ACCESS
    .filter((r) => pathname === r.path || pathname.startsWith(`${r.path}/`))
    .sort((a, b) => b.path.length - a.path.length)[0]
}

export function isRoleAllowed(role: AppRole | null, pathname: string): boolean {
  if (role == null) return false
  const access = findRouteAccess(pathname)
  if (!access) return true
  return access.roles.includes(role)
}
